import { useFieldState, useFormApi } from 'informed';
import { useRef } from 'react';
import { useReduxState } from 'hooks';
import { featureFlags } from 'shared-helpers/common';
import {
  Checkbox,
  FormGroup,
  FormSection,
  ImageUploader,
  LanguageVariants,
  NumberStepper,
  Text
} from 'components/common/form';
import { SiteSettingsForm } from '.';

const PlatformFeatures = () => {
  const { value: isCompetenciesOnly } = useFieldState<boolean>(
    'platformCompetenciesOnly'
  );

  const formApi = useFormApi();

  return (
    <FormGroup label={'Platform features'}>
      <Checkbox
        field="platformCompetenciesOnly"
        id="platform-competencies-only"
        label={'Enable competencies only'}
        onValueChange={(value: boolean) => {
          if (value === true) {
            formApi.setValue('hideAchievementsFromUI', true);
            formApi.setValue('hideCalendarEventsFromUI', true);
            formApi.setValue('hideCoursesFromUI', true);
            formApi.setValue('hidePathsFromUI', true);
          } else {
            formApi.setValue('hideAchievementsFromUI', false);
            formApi.setValue('hideCalendarEventsFromUI', false);
            formApi.setValue('hideCoursesFromUI', false);
            formApi.setValue('hidePathsFromUI', false);
          }
        }}
      />
      <Checkbox
        disabled={isCompetenciesOnly}
        field="hideAchievementsFromUI"
        id="hide-achievements"
        label={'Hide achievements from UI'}
        style={{ marginTop: 5 }}
      />
      <Checkbox
        disabled={isCompetenciesOnly}
        field="hideCalendarEventsFromUI"
        id="hide-calendar-events"
        label={'Hide calendar events from UI'}
        style={{ marginTop: 5 }}
      />
      <Checkbox
        disabled={isCompetenciesOnly}
        field="hideCoursesFromUI"
        id="hide-courses"
        label={'Hide courses from UI'}
        style={{ marginTop: 5 }}
      />
      <Checkbox
        disabled={isCompetenciesOnly}
        field="hidePathsFromUI"
        id="hide-paths"
        label={'Hide learning paths from UI'}
        style={{ marginTop: 5 }}
      />
    </FormGroup>
  );
};

const SiteSettingsCommon = () => {
  const { messages, serverConfig } = useReduxState();

  const faviconUploader = useRef<any>(undefined);

  const {
    enableUserSearch,
    featureFlags: featureFlagsConfig,
    globalDisableBackgroundJobs,
    globalDisableEmailSending,
    globalDisableJobs,
    hideAchievementsFromUI,
    hideCalendarEventsFromUI,
    hideCoursesFromUI,
    hidePathsFromUI,
    isDemoEnvironment,
    platformCompetenciesOnly,
    requireReasonWhenDeletingData,
    serverDisableIndexing,
    serverName,
    serverNameTranslations,
    siteFavicon,
    siteTitle,
    siteTitleTranslations,
    userSearchIncludeAllAdmins,
    userSearchIncludeAllUsers,
    usersAdminLimit,
    usersLimit,
    usersSuperadminLimit
  } = serverConfig;

  const initialValues = {
    enableUserSearch,
    featureFlags: featureFlagsConfig,
    globalDisableBackgroundJobs,
    globalDisableEmailSending,
    globalDisableJobs,
    hideAchievementsFromUI,
    hideCalendarEventsFromUI,
    hideCoursesFromUI,
    hidePathsFromUI,
    isDemoEnvironment,
    platformCompetenciesOnly,
    requireReasonWhenDeletingData,
    serverDisableIndexing,
    serverName,
    serverNameTranslations,
    siteFavicon,
    siteTitle,
    siteTitleTranslations,
    userSearchIncludeAllAdmins,
    userSearchIncludeAllUsers,
    usersAdminLimit,
    usersLimit,
    usersSuperadminLimit
  };

  const updateValuesBeforeSubmit = async (values) => {
    if (faviconUploader.current.state.imageAdded) {
      const file = await faviconUploader.current.upload();
      if (file) values.siteFavicon = file.url;
    }
    if (!values.siteFavicon) values.siteFavicon = null;
    return values;
  };

  const setFaviconUploader = (imageUploader) =>
    (faviconUploader.current = imageUploader);

  return (
    <div className="settings-common">
      <SiteSettingsForm
        initialValues={initialValues}
        padding={0}
        title={messages.siteSettings}
        updateValuesBeforeSubmit={updateValuesBeforeSubmit}
      >
        {() => (
          <div>
            <FormSection title={messages.settingsCourseSettings}>
              <FormGroup
                htmlFor="server-name"
                label={messages.siteSettingsServerName}
              >
                <Text field="serverName" id="server-name" />

                <LanguageVariants field="serverNameTranslations" type="text" />
              </FormGroup>

              <FormGroup
                htmlFor="site-title"
                label={messages.siteSettingsSiteTitle}
              >
                <Text field="siteTitle" id="site-title" />

                <LanguageVariants field="siteTitleTranslations" type="text" />
              </FormGroup>

              <FormGroup label={messages.siteSettingsSiteFavicon}>
                <ImageUploader
                  field="siteFavicon"
                  id="site-favicon"
                  maxWidth="32px"
                  messages={messages}
                  setUploader={setFaviconUploader}
                />
              </FormGroup>

              <FormGroup label={messages.siteSettingsUserLimits}>
                <NumberStepper
                  field="usersLimit"
                  id="users-limit"
                  label={messages.commonUsers}
                  min={0}
                  stepValue={100}
                  style={{ marginTop: 2 }}
                />
                <NumberStepper
                  field="usersAdminLimit"
                  id="users-admin-limit"
                  label={messages.usersAdminUsers}
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
                <NumberStepper
                  field="usersSuperadminLimit"
                  id="users-superadmin-limit"
                  label={messages.usersSuperadminUsers}
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
              </FormGroup>
            </FormSection>

            <FormSection title={messages.settingsFunctionality}>
              <FormGroup label={messages.siteSettingsIsDemoEnvironment}>
                <Checkbox
                  field="isDemoEnvironment"
                  id="is-demo-environment"
                  label={messages.siteSettingsIsDemoEnvironmentLabel}
                  labelHelpText={
                    messages.siteSettingsIsDemoEnvironmentDescription
                  }
                />
              </FormGroup>

              <PlatformFeatures />

              <FormGroup label="Feature flags">
                {featureFlags.map((flag) => (
                  <Checkbox
                    field={`featureFlags.${flag}`}
                    id={`feature-flag-${flag}`}
                    key={flag}
                    label={flag}
                    style={{ marginTop: 5 }}
                  />
                ))}
              </FormGroup>

              <FormGroup label="Disable functionality">
                <Checkbox
                  field="serverDisableIndexing"
                  id="server-disable-indexing"
                  label="Disable indexing"
                />
                <Checkbox
                  field="globalDisableEmailSending"
                  id="global-disable-email-sending"
                  label="Disable email sending"
                  style={{ marginTop: 5 }}
                />
                <Checkbox
                  field="globalDisableJobs"
                  id="global-disable-jobs"
                  label="Disable cron jobs"
                  style={{ marginTop: 5 }}
                />
                <Checkbox
                  field="globalDisableBackgroundJobs"
                  id="global-disable-background-jobs"
                  label="Disable background jobs"
                  style={{ marginTop: 5 }}
                />
              </FormGroup>

              <FormGroup label={messages.commonDeletionReason}>
                <Checkbox
                  field="requireReasonWhenDeletingData"
                  id="require-reason-for-deletion"
                  label="Require a reason when deleting selected data"
                  labelHelpText="When enabled, admins must provide a reason before performing certain deletions"
                />
              </FormGroup>

              <FormGroup label={messages.siteSettingsUserSearch}>
                <Checkbox
                  field="enableUserSearch"
                  id="enable-user-search"
                  label={messages.siteSettingsEnableUserSearch}
                />
                <Checkbox
                  field="userSearchIncludeAllUsers"
                  id="user-search-include-all-users"
                  label="Include all user accounts in user search for admins"
                  labelTooltip="Default is to include only administered users"
                  style={{ marginTop: 5 }}
                />
                <Checkbox
                  field="userSearchIncludeAllAdmins"
                  id="user-search-include-all-admins"
                  label="Include all admin accounts in user search when admin is adding an owner or admin"
                  labelTooltip="Default is to include only administered users"
                  style={{ marginTop: 5 }}
                />
              </FormGroup>
            </FormSection>
          </div>
        )}
      </SiteSettingsForm>
    </div>
  );
};

export default SiteSettingsCommon;
